/* eslint-disable camelcase */
import { Box, Button, Grid } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { updateInvoiceAddress, getInvoiceAddressById } from 'src/shared/services/invoiceAddressService';
import { toast } from 'react-toastify';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useSchemaEditInvoiceAddress } from '../schemas/useSchemaEditInvoiceAddress';
import { InvoiceAddressConfirmDeleteDialog } from './invoiceAddressConfirmDeleteDialog';

type InvoiceAddressEditModalProps = {
  modalOpen: boolean;
  invoiceAddress_id: number;
  onClose: () => void;
};

export const InvoiceAddressEditModal = (props: InvoiceAddressEditModalProps) => {
  const { modalOpen, invoiceAddress_id, onClose } = props;

  const isMounted = useIsMounted();

  const { setDateStamp } = useGlobals();

  const [loading, setLoading] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  const editInvoiceAddressRequestSchema = useSchemaEditInvoiceAddress();
  type EditInvoiceAddressFormInterface = InferType<typeof editInvoiceAddressRequestSchema>;

  const formMethods = useForm<EditInvoiceAddressFormInterface>({
    resolver: yupResolver(editInvoiceAddressRequestSchema),
    defaultValues: {
      name: '',
      address: '',
      postalcode: '',
      city: '',
      country: '',
      address2: '',
      email: '',
      name2: '',
    },
  });

  const { handleSubmit, reset } = formMethods;

  useEffect(() => {
    const fetchData = async () => {
      const result = await getInvoiceAddressById(invoiceAddress_id);
      if (isMounted()) {
        reset(result.data);
      }
    };

    fetchData();
  }, [invoiceAddress_id, isMounted, reset]);

  const saveFormData = useCallback(
    async (data: EditInvoiceAddressFormInterface) => {
      ConsoleHelper.log('data', data);
      setLoading(true);
      try {
        await updateInvoiceAddress(data);
        if (isMounted()) {
          toast.success(t('member:invoiceAddress.updateSuccess'));
          setDateStamp(new Date());
          onClose();
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('saveFormDataError:', e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, onClose, setDateStamp],
  );

  const onSubmit = useCallback(
    (data: EditInvoiceAddressFormInterface) => {
      saveFormData(data);
    },
    [saveFormData],
  );

  const onHandleSave = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <>
      <GenericDialog
        open={modalOpen}
        maxWidth='sm'
        onClose={() => onClose()}
        onSave={() => ConsoleHelper.log('save')}
        dialogTitle={t('member:invoiceAddress.editModalHeader')}
        padding={0}
        dialogContent={
          <Box
            sx={{
              position: 'relative',
              minHeight: '20rem',
            }}
          >
            <LoadingOverlay loading={loading} />
            <Box sx={{ p: 4 }}>
              <GridWrapper>
                <Grid item xs={12}>
                  <FormProvider {...formMethods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <ControlledTextField name='name' required label={t('member:invoiceAddress.name')} />
                        </Grid>
                        <Grid item xs={12}>
                          <ControlledTextField name='address' required label={t('member:invoiceAddress.address')} />
                        </Grid>
                        <Grid item xs={12}>
                          <ControlledTextField name='address2' label={t('member:invoiceAddress.address2')} />
                        </Grid>
                        <Grid item xs={12}>
                          <ControlledTextField name='postalcode' required label={t('member:invoiceAddress.postalcode')} />
                        </Grid>
                        <Grid item xs={12}>
                          <ControlledTextField name='city' required label={t('member:invoiceAddress.city')} />
                        </Grid>
                        <Grid item xs={12}>
                          <ControlledTextField name='country' label={t('member:invoiceAddress.country')} />
                        </Grid>
                        <Grid item xs={12}>
                          <ControlledTextField name='email' label={t('member:invoiceAddress.email')} />
                        </Grid>
                      </Grid>
                    </form>
                  </FormProvider>
                </Grid>
              </GridWrapper>
            </Box>
          </Box>
        }
        dialogActions={
          <Box display='flex' flexDirection='row' justifyContent='space-between' width='100%'>
            <Button disableElevation variant='contained' color='error' onClick={() => setShowConfirmDialog(true)} disabled={loading}>
              {t('common:button.delete')}
            </Button>
            <Box>
              <Button disableElevation variant='outlined' color='primary' onClick={onClose} disabled={loading} sx={{ mr: 1 }}>
                {t('common:button.cancel')}
              </Button>
              <Button disableElevation variant='contained' color='primary' onClick={onHandleSave} disabled={loading}>
                {t('common:button.update')}
              </Button>
            </Box>
          </Box>
        }
      />
      {showConfirmDialog && (
        <InvoiceAddressConfirmDeleteDialog
          invoiceAddress_id={invoiceAddress_id}
          callback={(isAborted: boolean) => {
            if (isAborted) {
              setDateStamp(new Date());
              onClose();
            }
            setShowConfirmDialog(false);
          }}
        />
      )}
    </>
  );
};
